import { Fragment, isValidElement } from 'react';
import {
  Typography,
  TypographyVariants,
  TypographyColors,
  TypographyElements,
  Badge,
  BadgeSizes,
} from '@carboledger/ui';
import { EMPTY_LIST } from '@/lib/utils';
import styles from './SummaryCard.module.scss';

const GRIDS = { 3: styles.gridThree, 5: styles.gridFive, 6: styles.gridSix };

/**
 * Props:
 *   label  the field's caption; may be a node
 *   value  text, or a node to render in its place
 *   color  overrides the value's own colour
 *   badge  { label, variant } shown beside the value
 */
const SummaryField = ({ label, value, color, badge }) => (
  <div className={styles.field}>
    <Typography
      as={TypographyElements.span}
      variant={TypographyVariants.caption}
      color={TypographyColors.placeholder}
    >
      {label}
    </Typography>
    {isValidElement(value) ? (
      value
    ) : (
      <div className={styles.valueRow}>
        <Typography
          as={TypographyElements.span}
          variant={TypographyVariants.bodySm}
          color={color ?? TypographyColors.primary}
        >
          {value}
        </Typography>
        {badge ? (
          <Badge variant={badge.variant} size={BadgeSizes.sm}>
            {badge.label}
          </Badge>
        ) : null}
      </div>
    )}
  </div>
);

/**
 * Props:
 *   heading  one section title, for a single-group card (rendered uppercase via CSS)
 *   fields   Array<{ id?, label, value, color, badge }> — `id` keys a row whose label repeats
 *   groups   Array<{ heading, fields }> — instead of heading/fields, for a card carrying
 *            several divider-headed groups, as the fulfill pages do
 *   columns  4 (default), 3, 5 or 6 across
 */
export const SummaryCard = ({ heading, fields = EMPTY_LIST, groups, columns = 4 }) => {
  // One shape internally; `groups` is the general case and heading/fields the single one.
  const sections = groups ?? [{ heading, fields }];
  const grid = GRIDS[columns] ?? styles.grid;

  return (
    <div className={styles.card}>
      {sections.map((section, index) => (
        <Fragment key={section.heading ?? index}>
          {section.heading ? (
            <div className={styles.groupHeader}>
              <Typography
                as={TypographyElements.span}
                variant={TypographyVariants.caption}
                color={TypographyColors.placeholder}
                className={styles.heading}
              >
                {section.heading}
              </Typography>
              {/* Only a multi-group card rules off its headings. */}
              {groups ? <span className={styles.groupRule} /> : null}
            </div>
          ) : null}

          <div className={grid}>
            {(section.fields ?? EMPTY_LIST).map((field) => (
              <SummaryField
                key={field.id ?? field.label}
                label={field.label}
                value={field.value}
                color={field.color}
                badge={field.badge}
              />
            ))}
          </div>
        </Fragment>
      ))}
    </div>
  );
};
